// apps-slider.js - Icon size slider control

import { getUserIconSizePreference, setUserIconSizePreference, LAYOUT_CONFIG, calculateMaximumSliderPercentage } from './apps-layout.js';
import * as render from './apps-render.js';
import * as resizeHandler from './apps-resize.js';
import state from './apps-state.js';

let sliderElement = null;
let wasMobile = window.innerWidth <= 768;

// Capture cursor semantic position before layout changes
let savedCursorRestoreData = null;

export function updateSliderPosition() {
    const isMobile = window.innerWidth <= 768;
    
    // If we crossed the mobile threshold, recreate the slider
    if (isMobile !== wasMobile) {
        wasMobile = isMobile;
        attachIconSizeSlider();
        return;
    }
    
    const slider = document.getElementById('iconSizeSliderContainer');
    if (!slider) return;
    
    const bottomPosition = isMobile ? '84px' : '20px';
    slider.style.bottom = bottomPosition;
}

export function createIconSizeSlider() {
    // Calculate dynamic max based on viewport
    const viewport = document.getElementById('appspaceViewport');
    const dynamicMax = calculateMaximumSliderPercentage(viewport.clientWidth, viewport.clientHeight);
    
    // Create slider container
    const container = document.createElement('div');
    container.id = 'iconSizeSliderContainer';
    container.dataset.dynamicMax = dynamicMax; // Store for later use
    
    // Check if mobile
    const isMobile = window.innerWidth <= 768;
    const bottomPosition = isMobile ? '84px' : '20px';
    const trackWidth = isMobile ? '60px' : '100px';
    const smallIconSize = isMobile ? '10px' : '12px';
    const largeIconSize = isMobile ? '16px' : '20px';
        
    container.style.cssText = `
        position: fixed;
        bottom: ${bottomPosition};
        right: 20px;
        z-index: 50;
        display: flex;
        align-items: center;
        gap: ${isMobile ? '6px' : '8px'};
        opacity: 0.7;
        transition: opacity 0.2s, bottom 0.2s;
        user-select: none;
        -webkit-user-select: none;
    `;
    
    container.addEventListener('mouseenter', () => {
        container.style.opacity = '1';
    });
    
    container.addEventListener('mouseleave', () => {
        container.style.opacity = '0.7';
    });
    
    // Small square icon (50%)
    const smallIcon = document.createElement('div');
    smallIcon.style.cssText = `
        width: ${smallIconSize};
        height: ${smallIconSize};
        background: rgba(255, 255, 255, 0.6);
        border: 1px solid rgba(0, 0, 0, 0.3);
        border-radius: 2px;
        flex-shrink: 0;
    `;
    
    // Slider track
    const track = document.createElement('div');
    track.style.cssText = `
        position: relative;
        width: ${trackWidth};
        height: 4px;
        background: rgba(255, 255, 255, 0.3);
        border-radius: 2px;
        cursor: pointer;
    `;
    
    // Tick marks
    const ticksContainer = document.createElement('div');
    ticksContainer.style.cssText = `
        position: absolute;
        top: -2px;
        left: 0;
        right: 0;
        height: 8px;
        pointer-events: none;
    `;
    
    // Create 5 tick marks (50%, 87.5%, 125%, 162.5%, 200%)
    for (let i = 0; i < 5; i++) {
        const tick = document.createElement('div');
        const percent = i / 4; // 0, 0.25, 0.5, 0.75, 1
        tick.style.cssText = `
            position: absolute;
            left: ${percent * 100}%;
            transform: translateX(-50%);
            width: 1px;
            height: 8px;
            background: rgba(255, 255, 255, 0.4);
        `;
        ticksContainer.appendChild(tick);
    }
    
    track.appendChild(ticksContainer);
    
    // Slider thumb
    const thumb = document.createElement('div');
    const thumbSize = isMobile ? '14px' : '16px';
    thumb.style.cssText = `
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        width: ${thumbSize};
        height: ${thumbSize};
        background: white;
        border: 2px solid rgba(0, 0, 0, 0.3);
        border-radius: 50%;
        cursor: grab;
        transition: transform 0.1s;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    `;

    // Set initial thumb position using dynamic max
    const currentPref = getUserIconSizePreference();
    const clampedPref = Math.min(currentPref, dynamicMax); // Clamp to dynamic max
    const initialPercent = (clampedPref - LAYOUT_CONFIG.sliderMin) / (dynamicMax - LAYOUT_CONFIG.sliderMin);
    thumb.style.left = `${initialPercent * 100}%`;
    
    track.appendChild(thumb);

    // Large square icon (200%)
    const largeIcon = document.createElement('div');
    largeIcon.style.cssText = `
        width: ${largeIconSize};
        height: ${largeIconSize};
        background: rgba(255, 255, 255, 0.6);
        border: 1px solid rgba(0, 0, 0, 0.3);
        border-radius: 3px;
        flex-shrink: 0;
    `;
    
    // Click handlers for icons
    smallIcon.style.cursor = 'pointer';
    smallIcon.addEventListener('click', () => {
        const current = getUserIconSizePreference();
        const newValue = Math.max(current - 0.125, LAYOUT_CONFIG.sliderMin);
        if (newValue !== current) {
            setUserIconSizePreference(newValue);
            
            // Update thumb position
            const percent = (newValue - LAYOUT_CONFIG.sliderMin) / (parseFloat(container.dataset.dynamicMax) - LAYOUT_CONFIG.sliderMin);
            thumb.style.left = `${percent * 100}%`;
            
            // Trigger layout recalculation with cursor preservation
            import('./apps-resize.js').then(resizeHandler => {
                resizeHandler.recalculateLayoutWithCursorPreservation();
            });
        }
    });

    largeIcon.style.cursor = 'pointer';
    largeIcon.addEventListener('click', () => {
        const current = getUserIconSizePreference();
        const dynamicMax = parseFloat(container.dataset.dynamicMax) || LAYOUT_CONFIG.sliderMax;
        const newValue = Math.min(current + 0.125, dynamicMax);
        if (newValue !== current) {
            setUserIconSizePreference(newValue);
            
            // Update thumb position
            const percent = (newValue - LAYOUT_CONFIG.sliderMin) / (dynamicMax - LAYOUT_CONFIG.sliderMin);
            thumb.style.left = `${percent * 100}%`;
            
            // Trigger layout recalculation with cursor preservation
            import('./apps-resize.js').then(resizeHandler => {
                resizeHandler.recalculateLayoutWithCursorPreservation();
            });
        }
    });
    
    // Assemble slider
    container.appendChild(smallIcon);
    container.appendChild(track);
    container.appendChild(largeIcon);

    // Slider interaction
    let isDragging = false;
    
    function updateSlider(clientX) {
        const rect = track.getBoundingClientRect();
        let percent = (clientX - rect.left) / rect.width;
        percent = Math.max(0, Math.min(1, percent));
        
        thumb.style.left = `${percent * 100}%`;
        
        // Use dynamic max instead of fixed max
        const currentDynamicMax = parseFloat(container.dataset.dynamicMax) || LAYOUT_CONFIG.sliderMax;
        const value = LAYOUT_CONFIG.sliderMin + (percent * (currentDynamicMax - LAYOUT_CONFIG.sliderMin));
        setUserIconSizePreference(value);
        
        // Just re-render during drag
        render.renderLaunchpad();
    }
    
    function startDrag(e) {
        isDragging = true;
        thumb.style.cursor = 'grabbing';
        
        // Capture cursor semantic position BEFORE layout changes
        import('./apps-kb-state.js').then(kbState => {
            import('./apps-kb-helpers.js').then(helpers => {
                const keyboardState = kbState.keyboardState;
                if (keyboardState.virtualCursor.active) {
                    const rank = keyboardState.virtualCursor.rank;
                    const file = keyboardState.virtualCursor.file;
                    const layout = render.calculateLayout();
                    const isFolder = !!state.openFolder;
                    const icons = helpers.getCurrentIcons();
                    const currentScreen = helpers.getCurrentScreen();
                    
                    // Save the semantic position (icon ID, insert point, etc.)
                    savedCursorRestoreData = resizeHandler.determineCursorRestoreStrategy(
                        rank, file, layout, isFolder, icons, currentScreen, helpers
                    );
                    
                    // Hide cursor visually
                    const cursorEl = document.getElementById('virtualCursor');
                    const arrowEl = document.getElementById('virtualCursorArrow');
                    if (cursorEl) cursorEl.style.display = 'none';
                    if (arrowEl) arrowEl.style.display = 'none';
                }
            });
        });
        
        updateSlider(e.clientX || e.touches[0].clientX);
        e.preventDefault();
    }
    
    function drag(e) {
        if (!isDragging) return;
        updateSlider(e.clientX || e.touches[0].clientX);
        e.preventDefault();
    }
    
    function endDrag() {
        if (!isDragging) return;
        isDragging = false;
        thumb.style.cursor = 'grab';
        
        // Restore cursor at correct position using saved semantic data
        if (savedCursorRestoreData) {
            import('./apps-kb-state.js').then(kbState => {
                const keyboardState = kbState.keyboardState;
                // Cursor should still be active (we only hid it visually)
                if (keyboardState.virtualCursor.active) {
                    // Restore using semantic position
                    resizeHandler.restoreCursorFromStrategy(savedCursorRestoreData);
                }
                savedCursorRestoreData = null;
            });
        }
    }
    
    // Mouse events
    track.addEventListener('mousedown', startDrag);
    thumb.addEventListener('mousedown', startDrag);
    document.addEventListener('mousemove', drag);
    document.addEventListener('mouseup', endDrag);
    
    // Touch events
    track.addEventListener('touchstart', startDrag, { passive: false });
    thumb.addEventListener('touchstart', startDrag, { passive: false });
    document.addEventListener('touchmove', drag, { passive: false });
    document.addEventListener('touchend', endDrag);
    
    sliderElement = container;
    return container;
}

export function attachIconSizeSlider() {
    // Remove existing slider if present
    const existing = document.getElementById('iconSizeSliderContainer');
    if (existing) {
        existing.remove();
        // Remove old resize listener
        window.removeEventListener('resize', updateSliderPosition);
    }
    
    // Create and attach new slider
    const newSlider = createIconSizeSlider();
    document.body.appendChild(newSlider);
    
    // Update position on resize (but throttle it)
    let resizeTimeout;
    const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            updateSliderPosition();
        }, 100);
    };
    
    window.addEventListener('resize', handleResize);
    
    return newSlider;
}

export function removeIconSizeSlider() {
    if (sliderElement) {
        sliderElement.remove();
        sliderElement = null;
    }
}

// Show/hide slider based on context
export function updateSliderVisibility(visible) {
    if (sliderElement) {
        sliderElement.style.display = visible ? 'flex' : 'none';
    }
}
